import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, ElementRef, AfterViewInit, signal, Input } from '@angular/core';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { Output, EventEmitter } from '@angular/core';
import { ct } from '@fullcalendar/core/internal-common';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';



/*===========================================================================================*/
/*CONSTANTES --------------------------------------------------------------------------------*/
/*===========================================================================================*/



const ConstEstados = {
    INICIAL: 'estado-inicial' as const,
    EDITANDO: 'estado-editando' as const,
    PROCESANDO: 'estado-procesando' as const,
    FINALIZADO: 'estado-finalizado' as const,
};

// 👇 Nueva constante: formatos de imagen soportados
export const SUPPORTED_IMAGE_FORMATS = {
    PNG: { mime: 'image/png', extension: '.png', label: 'PNG' },
    JPEG: { mime: 'image/jpeg', extension: '.jpg', label: 'JPEG/JPG' }
} as const;




/*===========================================================================================*/
/*TYPES -------------------------------------------------------------------------------------*/
/*===========================================================================================*/



export type Type_Estado = (typeof ConstEstados)[keyof typeof ConstEstados];

export type SupportedImageFormat = (typeof SUPPORTED_IMAGE_FORMATS)[keyof typeof SUPPORTED_IMAGE_FORMATS];



/*===========================================================================================*/
/*COMPONENT ---------------------------------------------------------------------------------*/
/*===========================================================================================*/



@Component({
    selector: 'p-upload-fotografia-digital',
    standalone: true,
    imports: [
        NgClass,
        CommonModule,
        DialogModule,
        FileUploadModule,
        ButtonModule,
        FormsModule
    ],
    templateUrl: './upload-fotografia-digital.component.html',
    styleUrls: ['./upload-fotografia-digital.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadCarnetComponent implements AfterViewInit {


    
    /*===========================================================================================*/
    /*INPUTS ------------------------------------------------------------------------------------*/
    /*===========================================================================================*/



    @Input() 
    public allowDownload: boolean = false;

    @Input()
    public onlyIcon: boolean = true;

    @Input()
    public disabled: boolean = false;

    @Input()
    public fileName: string = 'foto-carnet.png';



    /*===========================================================================================*/
    /*OUTPUTS -----------------------------------------------------------------------------------*/
    /*===========================================================================================*/



    @Output() public imageCropped = new EventEmitter<string>(); // Base64
    @Output() public imageCroppedFile = new EventEmitter<File>(); // Archivo File
    @Output() public onProcessFinished = new EventEmitter<void>(); // Evento de finalización del proceso

    

    /*===========================================================================================*/
    /*ACCESS TO VIEWS ---------------------------------------------------------------------------*/
    /*===========================================================================================*/



    @ViewChild('previewCanvas') previewCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;


    
    /*===========================================================================================*/
    /*VARIABLES ---------------------------------------------------------------------------------*/
    /*===========================================================================================*/

    
    
    /*SIGNALS -----------------------------------------------------------------------------------*/



    // Estado de carga del canvas
    public estado = signal<Type_Estado>(ConstEstados.INICIAL);

    // Controla la visibilidad del modal
    public showUploaderModal = signal<boolean>(false);

    // Controla la descarga de la imagen
    public downloadImageToo = signal<boolean>(false);


    
    /*PUBLIC ------------------------------------------------------------------------------------*/
    
    
    
    public ConstEstados = ConstEstados;



    
    /*PUBLIC - READONLY -------------------------------------------------------------------------*/
    
    
    
    // Configuración del canvas
    public readonly CANVAS_SIZE = 250; // Tamaño del canvas (300x300)
    public readonly CANVAS_WIDTH = this.CANVAS_SIZE;
    public readonly CANVAS_HEIGHT = this.CANVAS_SIZE;
    
    // Mostrar u ocultar el botón de descarga
    public readonly SHOW_DOWNLOAD_BUTTON = true;
    
    
    
    /*PRIVATES ----------------------------------------------------------------------------------*/



    // Contexto del canvas
    private ctx!: CanvasRenderingContext2D;

    // Propiedades para el arrastre
    private imageX = 0;
    private imageY = 0;
    private isDragging = false;
    private dragOffset = { x: 0, y: 0 };
    private imgInstance: HTMLImageElement | null = null;
    private CIRCLE_STROKE_COLOR = '#0067f8'; // Borde blanco semitransparente
    private CIRCLE_STROKE_COLOR_EDITANDO = '#060138ff'; 
    private CIRCLE_STROKE_COLOR_PROCESANDO = '#747474'; 
    private CIRCLE_STROKE_COLOR_FINALIZADO = '#0067f8'; 



    /*PRIVATES - READONLY -----------------------------------------------------------------------*/



    // Configuración del círculo guía
    private readonly CIRCLE_CENTER_X = this.CANVAS_SIZE / 2; // Centro X del canvas
    private readonly CIRCLE_CENTER_Y = this.CANVAS_SIZE / 2; // Centro Y
    private readonly CIRCLE_RADIUS = (this.CANVAS_SIZE / 2) * 1;   // Radio del círculo

    // Estilo del borde del círculo
    private readonly CIRCLE_STROKE_WIDTH = 7;
    private readonly CIRCLE_LINE_DASH: number[] = [/* 5, 5 */]; // Línea punteada
    private readonly CIRCLE_SHOW_STROKE = true;

    // Relleno interior del círculo
    private readonly CIRCLE_FILL_COLOR = 'rgba(255, 255, 255, 0)';
    private readonly CIRCLE_SHOW_FILL = true;

    // Color de fondo del canvas
    private readonly CANVAS_BACKGROUND_COLOR = '#f9f9f9'; // Gris claro

    // Colores del borde del canvas
    private readonly CANVAS_BORDER_COLOR_NORMAL = 'rgba(133, 133, 133, 0)'; // Borde normal (gris claro)
    private readonly CANVAS_BORDER_COLOR_AT_BORDER = '#0067f800'; // Borde cuando toca el límite


    
    /*===========================================================================================*/
    /*LIFECYCLE ---------------------------------------------------------------------------------*/
    /*===========================================================================================*/


    private initialDistance = 0;
    private lastTouchX = 0;
    private lastTouchY = 0;
    private isPinching = false;


    ngAfterViewInit(): void {
        const canvas = this.previewCanvas.nativeElement;
        this.ctx = canvas.getContext('2d')!;

        // Eventos de mouse para arrastre
        canvas.addEventListener('mousedown', this.onMouseDown);
        canvas.addEventListener('mousemove', this.onMouseMove);
        canvas.addEventListener('mouseup', this.onMouseUp);
        canvas.addEventListener('mouseleave', this.onMouseUp);
        canvas.addEventListener('wheel', this.onWheel);

        // 👇 Nuevos eventos táctiles
        canvas.addEventListener('touchstart', this.handleTouchStart);
        canvas.addEventListener('touchmove', this.handleTouchMove);
        canvas.addEventListener('touchend', this.handleTouchEnd);
    }

    handleTouchStart = (event: TouchEvent): void => {
        if (!this.imgInstance) return;

        const touches = Array.from(event.touches);
        const canvas = this.previewCanvas.nativeElement;
        const rect = canvas.getBoundingClientRect();

        if (touches.length === 1) {
            // Arrastre con un dedo
            const touch = touches[0];
            const touchX = touch.clientX - rect.left;
            const touchY = touch.clientY - rect.top;

            const imgWidth = this.imgInstance.width;
            const imgHeight = this.imgInstance.height;

            if (
                touchX >= this.imageX &&
                touchX <= this.imageX + imgWidth &&
                touchY >= this.imageY &&
                touchY <= this.imageY + imgHeight
            ) {
                this.isDragging = true;
                this.dragOffset = {
                    x: touchX - this.imageX,
                    y: touchY - this.imageY,
                };
            }

            this.lastTouchX = touchX;
            this.lastTouchY = touchY;

        } else if (touches.length === 2) {
            // Zoom con dos dedos
            this.isPinching = true;
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            this.initialDistance = Math.hypot(dx, dy);
        }
    };


    handleTouchMove = (event: TouchEvent): void => {
        event.preventDefault(); // Evita scrolling mientras se toca

        const canvas = this.previewCanvas.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const touches = Array.from(event.touches);

        if (touches.length === 1 && this.isDragging) {
            // Arrastrar con un dedo
            const touch = touches[0];
            const touchX = touch.clientX - rect.left;
            const touchY = touch.clientY - rect.top;

            this.imageX = touchX - this.dragOffset.x;
            this.imageY = touchY - this.dragOffset.y;
            this.enforceVisibility();
            this.drawImage();

        } else if (touches.length === 2 && this.isPinching) {
            // Zoom multitáctil
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            const distance = Math.hypot(dx, dy);

            const scaleSpeed = 0.01;
            const delta = (distance - this.initialDistance) * scaleSpeed;
            this.initialDistance = distance;

            const img = this.imgInstance!;
            let newWidth = img.width + delta * img.width;
            let newHeight = img.height + delta * img.height;

            newWidth = Math.max(20, newWidth);
            newHeight = Math.max(20, newHeight);

            const scaleRatio = newWidth / img.width;
            const offsetX = (touches[0].clientX + touches[1].clientX) / 2 - rect.left;
            const offsetY = (touches[0].clientY + touches[1].clientY) / 2 - rect.top;

            const dxCenter = offsetX - this.imageX - img.width / 2;
            const dyCenter = offsetY - this.imageY - img.height / 2;

            this.imageX += dxCenter * (scaleRatio - 1);
            this.imageY += dyCenter * (scaleRatio - 1);

            img.width = newWidth;
            img.height = newHeight;

            this.enforceVisibility();
            this.drawImage();
        }
    };


    handleTouchEnd = (_event: TouchEvent): void => {
        this.isDragging = false;
        this.isPinching = false;
    };


    
    /*===========================================================================================*/
    /*METHODS -----------------------------------------------------------------------------------*/
    /*===========================================================================================*/


    
    /*MAINS -------------------------------------------------------------------------------------*/



    drawImage(): void {
        const canvas = this.previewCanvas.nativeElement;
        const ctx = this.ctx;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (this.imgInstance) {
            const imgWidth = this.imgInstance.width;
            const imgHeight = this.imgInstance.height;

            // Paso 1: Dibujar fondo del canvas
            ctx.fillStyle = this.CANVAS_BACKGROUND_COLOR;
            ctx.fillRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);

            // Paso 2: Crear clip path en forma de círculo
            ctx.beginPath();
            ctx.arc(
                this.CIRCLE_CENTER_X,
                this.CIRCLE_CENTER_Y,
                this.CIRCLE_RADIUS,
                0,
                Math.PI * 2
            );
            ctx.clip(); // Limitar dibujo solo dentro del círculo

            // Paso 3: Dibujar imagen dentro del clip path
            ctx.drawImage(this.imgInstance, this.imageX, this.imageY, imgWidth, imgHeight);

            // Paso 4: Restablecer el clip para poder dibujar sobre el borde del círculo
            ctx.restore();

            // Paso 5: Dibujar guía circular encima
            ctx.beginPath();
            ctx.arc(
                this.CIRCLE_CENTER_X,
                this.CIRCLE_CENTER_Y,
                this.CIRCLE_RADIUS,
                0,
                Math.PI * 2
            );

            if (this.CIRCLE_SHOW_STROKE) {
                ctx.lineWidth = this.CIRCLE_STROKE_WIDTH;
                ctx.strokeStyle = this.CIRCLE_STROKE_COLOR;
                ctx.setLineDash(this.CIRCLE_LINE_DASH);
                ctx.stroke();
                ctx.setLineDash([]); // Resetear estilo de línea
            }

            if (this.CIRCLE_SHOW_FILL) {
                ctx.fillStyle = this.CIRCLE_FILL_COLOR;
                ctx.fill();
            }

            // Feedback visual si la imagen está pegada al borde
            const isAtBorder =
                this.imageX <= 0 ||
                this.imageY <= 0 ||
                this.imageX + imgWidth >= this.CANVAS_WIDTH ||
                this.imageY + imgHeight >= this.CANVAS_HEIGHT;

            canvas.style.borderColor = isAtBorder ? this.CANVAS_BORDER_COLOR_AT_BORDER : this.CANVAS_BORDER_COLOR_NORMAL;
            canvas.style.transition = 'border-color 0.3s ease';
        }
    }



    /*ON ----------------------------------------------------------------------------------------*/



    onFileSelected(event: Event): void {
        this.estado.set(ConstEstados.EDITANDO);
        this.updateCircleStrokeColor(this.CIRCLE_STROKE_COLOR_EDITANDO);


        
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const file = input.files[0];

            // ✅ Validación de formato permitido
            const allowedMimes = Object.values(SUPPORTED_IMAGE_FORMATS).map(f => f.mime);
            if (!allowedMimes.includes(file.type as SupportedImageFormat['mime'])) {
                alert('Solo se permiten imágenes en formato PNG o JPG.');
                return;
            }

            const reader = new FileReader();

            reader.onload = (e: ProgressEvent<FileReader>) => {
                const img = new Image();
                img.onload = () => {
                    this.imgInstance = img;

                    // Si la imagen es más pequeña que el canvas, centrarla
                    const canvasWidth = this.CANVAS_SIZE;
                    const canvasHeight = this.CANVAS_SIZE;
                    const scaleX = canvasWidth / img.width;
                    const scaleY = canvasHeight / img.height;
                    const scale = Math.min(scaleX, scaleY, 1); // Máximo escalar hasta llenar canvas

                    const scaledWidth = img.width * scale;
                    const scaledHeight = img.height * scale;

                    this.imageX = (canvasWidth - scaledWidth) / 2;
                    this.imageY = (canvasHeight - scaledHeight) / 2;

                    this.drawImage();
                };
                img.src = e.target?.result as string;
            };

            reader.readAsDataURL(input.files[0]);
        }
    }

    onMouseDown = (event: MouseEvent): void => {
        if (!this.imgInstance) return;

        const canvas = this.previewCanvas.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const imgWidth = this.imgInstance.width;
        const imgHeight = this.imgInstance.height;

        // Verificar si el click fue dentro de la imagen
        if (
            mouseX >= this.imageX &&
            mouseX <= this.imageX + imgWidth &&
            mouseY >= this.imageY &&
            mouseY <= this.imageY + imgHeight
        ) {
            this.isDragging = true;
            this.dragOffset = {
                x: mouseX - this.imageX,
                y: mouseY - this.imageY,
            };
        }
    };

    onMouseMove = (event: MouseEvent): void => {
        if (!this.isDragging || !this.imgInstance) return;

        const canvas = this.previewCanvas.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        this.imageX = mouseX - this.dragOffset.x;
        this.imageY = mouseY - this.dragOffset.y;

        // En vez de repulsión magnética, forzamos los límites
        //this.enforceBoundary();
        this.enforceVisibility();

        this.drawImage();
    };

    onMouseUp = (): void => {
        this.isDragging = false;

        // Centrar automáticamente la imagen en el círculo
        /* if (this.imgInstance) {
            const imgWidth = this.imgInstance.width;
            const imgHeight = this.imgInstance.height;

            this.imageX = this.CIRCLE_CENTER_X - imgWidth / 2;
            this.imageY = this.CIRCLE_CENTER_Y - imgHeight / 2;

            this.enforceVisibility();
            this.drawImage();
        } */
    };

    onWheel = (event: WheelEvent): void => {
        if (!this.imgInstance) return;

        event.preventDefault();

        const zoomSpeed = 0.1;
        const img = this.imgInstance;
        const { offsetX, offsetY } = event;

        // Escalar el tamaño manteniendo las proporciones
        let newWidth = img.width + (event.deltaY < 0 ? img.width * zoomSpeed : -img.width * zoomSpeed);
        let newHeight = img.height + (event.deltaY < 0 ? img.height * zoomSpeed : -img.height * zoomSpeed);

        // Limitar escala mínima
        newWidth = Math.max(20, newWidth);
        newHeight = Math.max(20, newHeight);

        // Calcular desplazamiento para zoom centrado en el puntero
        const scaleRatio = newWidth / img.width;
        const dx = (offsetX - this.imageX) * (scaleRatio - 1);
        const dy = (offsetY - this.imageY) * (scaleRatio - 1);

        this.imageX -= dx;
        this.imageY -= dy;

        // Actualizar tamaño
        img.width = newWidth;
        img.height = newHeight;

        //this.enforceBoundary();
        this.enforceVisibility();
        this.drawImage();
    };



    /*===========================================================================================*/
    /*UTILS -------------------------------------------------------------------------------------*/
    /*===========================================================================================*/



    /*PRIVATES ----------------------------------------------------------------------------------*/



    private enforceVisibility(): void {
        const canvas = this.previewCanvas.nativeElement;
        const img = this.imgInstance;

        if (!img) return;

        const imgWidth = img.width;
        const imgHeight = img.height;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // La imagen debe tener al menos la mitad visible en cada eje
        const minVisibleX = -imgWidth + canvasWidth / 2;
        const maxVisibleX = canvasWidth - imgWidth / 2;

        const minVisibleY = -imgHeight + canvasHeight / 2;
        const maxVisibleY = canvasHeight - imgHeight / 2;

        // Ajustar límites visibles
        this.imageX = Math.max(minVisibleX, Math.min(this.imageX, maxVisibleX));
        this.imageY = Math.max(minVisibleY, Math.min(this.imageY, maxVisibleY));
    }

    private enforceBoundary(): void {
        const canvas = this.previewCanvas.nativeElement;
        const img = this.imgInstance;

        if (!img) return;

        const imgWidth = img.width;
        const imgHeight = img.height;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Limitar movimiento horizontal
        if (this.imageX < 0) {
            this.imageX = 0;
        } else if (this.imageX + imgWidth > canvasWidth) {
            this.imageX = canvasWidth - imgWidth;
        }

        // Limitar movimiento vertical
        if (this.imageY < 0) {
            this.imageY = 0;
        } else if (this.imageY + imgHeight > canvasHeight) {
            this.imageY = canvasHeight - imgHeight;
        }
    }

    private updateCircleStrokeColor(color: string): void {
        this.CIRCLE_STROKE_COLOR = color;
        this.drawImage(); // Volver a dibujar el canvas
    }


    
    /*PUBLICS -----------------------------------------------------------------------------------*/


    
    // Método para abrir el modal
    public openImageUploaderModal(): void {
        this.showUploaderModal.set(true);
        // Reiniciar el estado si es necesario
        this.estado.set(ConstEstados.INICIAL);
    }

    // Método para cerrar el modal
    public closeUploaderModal(): void {
        this.showUploaderModal.set(false);
        this.resetCanvas(); // Limpiar el canvas al cerrar
    }

    // Método para establecer si se permite la descarga de la imagen
    public setAllowDownload(value: boolean): void {
        this.downloadImageToo.set(value);
    }

    // Método para manejar el cambio del checkbox de descarga
    public onDownloadCheckboxChange(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        if (input && typeof input.checked === 'boolean') {
            this.setAllowDownload(input.checked);
        }
    }

    //Limpia el canvas y restablece todos los valores al estado inicial
    private resetCanvas(): void {
        const canvas = this.previewCanvas.nativeElement;
        const ctx = this.ctx;

        if (ctx) {
            // 1. Restablecer transformaciones y clip
            ctx.setTransform(1, 0, 0, 1, 0, 0); // Resetear transformaciones
            ctx.globalAlpha = 1; // Resetear opacidad
            ctx.beginPath(); // Limpiar cualquier path activo

            // 2. Limpiar completamente el canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // 3. Dibujar fondo sólido para evitar residuos visuales
            ctx.fillStyle = this.CANVAS_BACKGROUND_COLOR;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < 10; i++) {
                ctx.beginPath();
                ctx.arc(
                    this.CIRCLE_CENTER_X,
                    this.CIRCLE_CENTER_Y,
                    this.CIRCLE_RADIUS + i * 2,
                    0,
                    0
                );
                ctx.strokeStyle = 'rgba(0, 0, 0, 0)';
                ctx.lineWidth = 0;
            }
            
            // 4. Reiniciar propiedades
            this.imageX = 0;
            this.imageY = 0;
            this.imgInstance = null;
            this.CIRCLE_STROKE_COLOR = '#0067f8'; // Color inicial del borde guía

            // Si usas signal()
            this.downloadImageToo.set(false); // Opcional: desactivar descarga automática
            this.fileInput.nativeElement.value = ''; // Limpiar el input de archivo

            this.estado.set(ConstEstados.INICIAL); // Restablecer estado
        }
    }

    // Método para procesar la imagen y emitirla
    public processAndEmitImage(): void {
        this.estado.set(ConstEstados.PROCESANDO);
        this.updateCircleStrokeColor(this.CIRCLE_STROKE_COLOR_PROCESANDO);

        if (!this.imgInstance) return;

        const originalCanvas = this.previewCanvas.nativeElement;
        const ctx = originalCanvas.getContext('2d');
        if (!ctx) return;

        // Crear canvas temporal para recortar
        const cropCanvas = document.createElement('canvas');
        const cropCtx = cropCanvas.getContext('2d');
        if (!cropCtx) return;

        const CIRCLE_CENTER_X = this.CIRCLE_CENTER_X;
        const CIRCLE_CENTER_Y = this.CIRCLE_CENTER_Y;
        const CIRCLE_RADIUS = this.CIRCLE_RADIUS - this.CIRCLE_STROKE_WIDTH;

        cropCanvas.width = this.CANVAS_WIDTH;
        cropCanvas.height = this.CANVAS_HEIGHT;

        cropCtx.clearRect(0, 0, cropCanvas.width, cropCanvas.height);
        cropCtx.save();
        cropCtx.beginPath();
        cropCtx.arc(CIRCLE_CENTER_X, CIRCLE_CENTER_Y, CIRCLE_RADIUS, 0, Math.PI * 2);
        cropCtx.closePath();
        cropCtx.clip();

        cropCtx.drawImage(originalCanvas, 0, 0, originalCanvas.width, originalCanvas.height);
        cropCtx.restore();

        setTimeout(() => {
            this.estado.set(ConstEstados.FINALIZADO);
            this.updateCircleStrokeColor(this.CIRCLE_STROKE_COLOR_FINALIZADO);
        }, 3000);

        setTimeout(() => {
            const base64Image = cropCanvas.toDataURL('image/png');

            // Emitir base64
            this.imageCropped.emit(base64Image);

            // Convertir a File
            cropCanvas.toBlob((blob) => {
                if (blob) {
                    const fileName = this.fileName;
                    const file = new File([blob], fileName, { type: 'image/png' });

                    // 🔥 Crear objectURL compatible con PrimeNG
                    const objectURL = URL.createObjectURL(blob);

                    // Extender el File con objectURL (solo si es necesario)
                    const extendedFile = new File([blob], fileName, { type: 'image/png' });
                    (extendedFile as any).objectURL = {
                        changingThisBreaksApplicationSecurity: objectURL
                    };

                    // Emitir el archivo extendido
                    this.imageCroppedFile.emit(extendedFile);

                    // Opcional: descargar si está activado
                    if (this.allowDownload && this.downloadImageToo()) {
                        const link = document.createElement('a');
                        link.download = fileName;
                        link.href = base64Image;
                        link.click();
                    }
                }
            }, 'image/png');

            this.onProcessFinished.emit(); // Emitir evento de finalización del proceso
            this.closeUploaderModal(); // Cierra el modal
        }, 5000);
    }



}